/**
 * Comprehensive debug logger to track down the CMS redirect issue
 */

class DebugLogger {
  private static instance: DebugLogger;
  private logs: Array<{ timestamp: string; type: string; message: string; data?: any }> = [];

  static getInstance() {
    if (!DebugLogger.instance) {
      DebugLogger.instance = new DebugLogger();
    }
    return DebugLogger.instance;
  }

  log(type: string, message: string, data?: any) {
    const timestamp = new Date().toISOString();
    const logEntry = { timestamp, type, message, data };
    this.logs.push(logEntry);
    
    // Always log to console with clear prefix
    console.log(`🔍 [DEBUG-${type}] ${timestamp}: ${message}`, data || '');
    
    // Keep only last 100 logs to prevent memory issues
    if (this.logs.length > 100) {
      this.logs = this.logs.slice(-100);
    }
  }

  getLogs() {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
  }
}

export const debugLogger = DebugLogger.getInstance();

// Global window object monitoring - SAFE VERSION
if (typeof window !== 'undefined') {
  // Store original methods without overriding them
  const originalReload = window.location.reload.bind(window.location);
  const originalAssign = window.location.assign.bind(window.location);
  const originalReplace = window.location.replace.bind(window.location);
  
  // Override using defineProperty to avoid read-only errors
  try {
    Object.defineProperty(window.location, 'reload', {
      value: function(...args: any[]) {
        debugLogger.log('LOCATION', 'window.location.reload() called!', {
          stack: new Error().stack,
          args
        });
        return originalReload.apply(this, args);
      },
      writable: true,
      configurable: true
    });
  } catch (e) {
    debugLogger.log('LOCATION', 'Could not override location.reload - using event monitoring instead', { error: e });
  }
  
  // Track visibility changes
  document.addEventListener('visibilitychange', () => {
    debugLogger.log('VISIBILITY', `Page visibility changed to: ${document.visibilityState}`);
  });
  
  // Track focus/blur
  window.addEventListener('focus', () => {
    debugLogger.log('FOCUS', 'Window focused');
  });
  
  window.addEventListener('blur', () => {
    debugLogger.log('FOCUS', 'Window blurred');
  });
  
  // Track beforeunload
  window.addEventListener('beforeunload', (e) => {
    debugLogger.log('UNLOAD', 'beforeunload event triggered', {
      returnValue: e.returnValue,
      stack: new Error().stack
    });
  });
  
  // Track unload
  window.addEventListener('unload', () => {
    debugLogger.log('UNLOAD', 'unload event triggered');
  });
  
  // Track any navigation changes
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;
  
  history.pushState = function(...args) {
    debugLogger.log('NAVIGATION', 'history.pushState called', { args });
    return originalPushState.apply(this, args);
  };
  
  history.replaceState = function(...args) {
    debugLogger.log('NAVIGATION', 'history.replaceState called', { args });
    return originalReplaceState.apply(this, args);
  };
}
